import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Grid,
  Stack,
  Divider,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Settings as SettingsIcon,
  Lock as LockIcon,
  Key as KeyIcon,
  Circle as CircleIcon,
  DarkMode as MoonIcon,
  AccountCircle as AccountCircleIcon,
  Folder as FolderIcon,
  List as ListIcon,
  TextFields as TypeIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { BglPrimaryButton, BglPrimaryOutlinedButton,BglCloseOutlinedSmallButton, BglSecondarySmallButton, BglPrimaryOutlinedButtonBase } from 'components/button/BglButtons';
import { useGlobalConfig } from 'context/GlobalContext';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { Shapes } from 'lucide-react';
import { KeyRound } from 'lucide-react';
import { Lock } from 'lucide-react';
import { CirclePlus } from 'lucide-react';
import { ArrowRight } from 'lucide-react';


// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-2px)',
  },
}));

const FilterChip = styled(Chip)<{ selected?: boolean }>(({ theme, selected }) => ({
  borderRadius: 8,
  fontWeight: 500,
  border: selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
  backgroundColor: selected ? theme.palette.primary.main : 'transparent',
  color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: selected ? theme.palette.primary.dark : theme.palette.action.hover,
  },
}));

const StatusIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
  borderRadius: 6,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
}));

// Mock data
const mockClusters = [
  {
    id: 'clus_2b3c4d5e6f7g8h9i',
    name: 'EE TREATMENT',
    type: 'Physical',
    categories: ['Program', 'Feature', 'Content'],
    userCount: 150000,
    isActive: true,
  },
  {
    id: 'clus_4d5e6f7g8h9i0j1k',
    name: 'EV USERS',
    type: 'Virtual',
    categories: ['Feature', 'Template'],
    userCount: null,
    isActive: true,
  },
  {
    id: 'clus_ab12cd34ef56gh78',
    name: 'AMI',
    type: 'Virtual',
    categories: ['Template', 'Feature'],
    userCount: null,
    isActive: true,
  },
  {
    id: 'clus_bc23de45fg67hi89',
    name: 'AMR',
    type: 'Virtual',
    categories: ['Template', 'Feature'],
    userCount: null,
    isActive: true,
  },
  {
    id: 'clus_cd34ef56gh78ij90',
    name: 'NSM',
    type: 'Virtual',
    categories: ['Template', 'Feature'],
    userCount: null,
    isActive: true,
  },
];

const categoryCounts = {
  All: 12,
  Program: 2,
  Feature: 5,
  Template: 8,
  Content: 4,
};

export const DCXClustersPage: React.FC = () => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  const [selectedCategory, setSelectedCategory] = useState('Feature');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const filteredClusters = mockClusters.filter(cluster => {
    const matchesSearch = cluster.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cluster.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || cluster.categories.includes(selectedCategory);
    const matchesActiveFilter = showInactive || cluster.isActive;
    
    return matchesSearch && matchesCategory && matchesActiveFilter;
  });

  const getTypeColor = (type: string) => {
    return type === 'Physical' ? theme.palette.primary.main : theme.palette.grey[500];
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      Program: theme.palette.primary.main,
      Feature: theme.palette.primary.main,
      Template: theme.palette.primary.main,
      Content: theme.palette.grey[500],
    };
    return colors[category] || theme.palette.grey[500];
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      p: 3,
    }}>
      {/* Configuration Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Shapes size={18} style={{ marginRight: '8px', color: 'primary.main' }} />
                <Typography variant="subtitle1">
                  Cluster Configuration
                </Typography>
              </Box>
              <Typography variant="body2"  sx={{ mb: 3,textAlign:"left",fontSize:"12px", height:'30px' }}>
                Define and manage Dynamic Customer Experience (DCX) clusters.
              </Typography>
              <Button
                variant='outlined'
                startIcon={<CirclePlus size={16}/>}
                fullWidth
                sx={{ borderRadius: '6px',minHeight: '34px',
                        color: 'text.secondary',
                        borderColor: 'divider'
                }}
                onClick={() => rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS_CREATE)}
              >
                Create Cluster
              </Button>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <KeyRound size={18} style={{ marginRight: '8px', color: 'primary.main' }} />
                <Typography variant="subtitle1" >
                  Feature Access
                </Typography>
              </Box>
              <Typography variant="body2"  sx={{ mb: 3,textAlign:"left",fontSize:"12px" ,height:'30px' }}>
                Manage feature access based on 'Feature' clusters.
              </Typography>
              <Button
                variant='outlined'
                endIcon={<ArrowRight size={16} />}
                fullWidth
                sx={{ borderRadius: '6px', minHeight:'34px',color: 'text.secondary',
                    borderColor: 'divider'}}
                onClick={() => rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.FEATURE_ACCESS)}
              >
                Configure
              </Button>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Lock size={18} style={{ marginRight: '8px', color: 'primary.main' }} />
                <Typography variant="subtitle1">
                  Template Personalization
                </Typography>
              </Box>
              <Typography variant="body2"  sx={{ mb: 3,textAlign:"left" ,fontSize:"12px" ,height:'30px' }}>
                Apply template logic based on 'Template' clusters.
              </Typography>
              <Button
                variant='outlined'
                endIcon={<ArrowRight size={16}/>}
                fullWidth
                sx={{ borderRadius: '6px', minHeight:'34px' ,color: 'text.secondary',
                    borderColor: 'divider'}}
              >
                Configure
              </Button>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Cluster List Section */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',textAlign:"left" }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Cluster List
            </Typography>
            <Typography variant="body2"  sx={{fontSize:"12px" ,height:'30px' }}>
              A list of all defined clusters. Use the category boxes to filter the list below.
            </Typography>
          </Box>

          {/* Filter Categories */}
          <Box sx={{ mb: 3}}>
            <Stack direction="row" spacing={1} useFlexGap sx={{justifyContent:'space-between' }}>
              {Object.entries(categoryCounts).map(([category, count]) => {
                const getCategoryIcon = (cat: string) => {
                  switch (cat) {
                    case 'All': return <ListIcon />;
                    case 'Program': return <FolderIcon />;
                    case 'Feature': return <SettingsIcon />;
                    case 'Template': return <ListIcon />;
                    case 'Content': return <TypeIcon />;
                    default: return <CircleIcon />;
                  }
                };

                return (
                  <Box key={category} sx={{ position: 'relative',width: '-webkit-fill-available'}}>
                    {/* <FilterChip
                      label={`${category} (${count})`}
                      icon={
                        <>
                          {getCategoryIcon(category)}
                          <Tooltip title={`Filter by ${category} clusters`} arrow placement="top">
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                width: 16,
                                height: 16,
                                bgcolor: 'background.paper',
                                border: 1,
                                borderColor: 'divider',
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                }
                              }}
                            >
                              <InfoIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                            </IconButton>
                         </Tooltip>
                        </>
                      }
                      selected={selectedCategory === category}
                      onClick={() => setSelectedCategory(category)}
                      clickable
                      sx={{ 
                        height:'100px',
                        '& .MuiChip-icon': { 
                          fontSize: '18px',
                          color: selectedCategory === category ? 'inherit' : 'text.secondary'
                        }
                      }}
                    /> */}

                    <Box style={{display:'flex',flexDirection:'column',alignItems:'center',gap:12,borderRadius:8,padding:'16px',border:'1px solid #E0E0E0', height:'100px', width:'100%'}}>
                      <Box style={{display:'flex',alignItems:'center',gap:1,justifyContent:'space-between',width:'100%'}}>
                      {getCategoryIcon(category)}
                      <Tooltip title={`Filter by ${category} clusters`} arrow placement="top">
                        <IconButton
                          size="small"
                          sx={{
                            width: 16,
                            height: 16,
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            }
                          }}
                        >
                          <InfoIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                        </IconButton>
                      </Tooltip>
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 500,textAlign:'center' }}>{`${category} (${count})`}</Typography>
                      </Box>
                    </Box>
                    
                  </Box>
                );
              })}
            </Stack>
          </Box>

          {/* Search and Toggle */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center',
            mb: 3,
            gap: 2,
            flexWrap: 'wrap'
          }}>
            <TextField
              placeholder="Search clusters..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ width:'100%' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  size="small"
                />
              }
              label="Show inactive clusters"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          </Box>

          {/* Cluster Table */}
          {filteredClusters.length > 0 ? (
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${theme.palette.divider}` }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'background.default' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Cluster Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cluster ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Categories</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>User Count</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredClusters.map((cluster) => (
                    <TableRow key={cluster.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {cluster.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {cluster.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cluster.type}
                          size="small"
                          sx={{
                            bgcolor: getTypeColor(cluster.type),
                            color: 'white',
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {cluster.categories.map((category) => (
                            <Chip
                              key={category}
                              label={category}
                              size="small"
                              sx={{
                                bgcolor: getCategoryColor(category),
                                color: 'white',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                              }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {cluster.userCount ? cluster.userCount.toLocaleString() : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit cluster">
                          <IconButton size="small">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8, 
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              bgcolor: 'background.default'
            }}>
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                No clusters found
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                {searchTerm ? 'Try adjusting your search criteria' : 'No clusters match the selected category'}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS_CREATE)}
                sx={{ textTransform: 'none' }}
              >
                Create First Cluster
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
